import Row from 'components/core/Row'
import Typography from 'components/core/Typography'
import Column from 'components/core/Column'
import { Table } from '../Table'
import { Trash2, File as FileIcon, Paperclip } from 'lucide-react'
import { IconAnchorTag, IconButton } from 'components/core/IconButton'
import { openDialog } from 'hooks/useDialogManager'
import { TextCell } from 'components/cells/TextCell'
import { useMemo, useState } from 'react'
import Popover from 'components/core/Popover'
import { SPACING } from 'constants/spacing.constants'
import styled from '@emotion/styled'
import { AnchorTag } from 'components/core/AnchorTag'
import Select from 'components/core/Select'
import { useGetThreadDocumentsById } from 'queries/thread.queries'
import { Skeleton } from 'components/core/Skeleton'
import Button from 'components/core/Button'
import { downloadFile } from 'utils/downloadBlob.utils'
import Separator from 'components/core/Separator'
import { DESIGN_TOKENS } from 'constants/color.constants'
import { Doc } from 'types/doc.types'
import { useGenerateDocPdf } from 'mutations/doc.mutations'
import { typedArrayIncludes } from 'utils/type.utils'

const RowStyled = styled(Row)`
    min-height: ${SPACING.p4};
    padding-left: ${SPACING.p2};
    padding-right: ${SPACING.p2};
`

const AnchorTagStyled = styled(AnchorTag)`
    width: 100%;
    padding: ${SPACING.p1};

    &:hover {
        background-color: ${DESIGN_TOKENS.background.light};
    }
`

const StyledPopoverColumn = styled(Column)`
    min-width: 500px;
    max-width: 800px;
`

export const ThreadFileAttachmentsPanel = ({
    isReadOnly,
    customerId,
    threadId,
    documents: defaultDocuments,
    onChangeSelectedThreadDocuments,
}: {
    isReadOnly?: boolean
    customerId?: string | null
    threadId: string
    documents: Doc[]
    onChangeSelectedThreadDocuments: (documents: Doc[]) => void
}) => {
    const [selectedThreadDocuments, setSelectedThreadDocuments] = useState<
        Doc[]
    >([])
    const [popupState, setPopupState] = useState<'options' | 'pickDocument'>(
        'options'
    )
    const { mutateAsync: getPdfBlob } = useGenerateDocPdf()

    const {
        isLoading: isLoadingDocuments,
        data: documents = [],
        refetch: refetchDocuments,
    } = useGetThreadDocumentsById({
        threadId,
        hasOrderDocuments: true,
    })

    const documentOptions = useMemo(() => {
        return documents.map((document) => ({
            label:
                document.entityType === 'order'
                    ? `${document.meta.orderFullId}: ${document.fileName}`
                    : `Thread: ${document.fileName}`,
            value: document.id,
        }))
    }, [documents])

    const _onChangeSelectedThreadDocuments = (
        selectedDocumentIds: string[]
    ) => {
        setSelectedThreadDocuments(
            documents.filter((document) =>
                selectedDocumentIds.includes(document.id)
            )
        )
    }

    const _onAttachSelectedDocuments = (onClose?: () => void) => () => {
        onChangeSelectedThreadDocuments(selectedThreadDocuments)
        onClose?.()
    }

    const _onRemoveThreadDocument = (id: string) => {
        onChangeSelectedThreadDocuments(
            defaultDocuments.filter((document) => document.id !== id)
        )
    }

    const _onShowPickDocument = () => {
        setSelectedThreadDocuments(defaultDocuments)
        setPopupState('pickDocument')
    }

    const _onSuccessUpload = async (args?: { documents: Doc[] }) => {
        onChangeSelectedThreadDocuments([
            ...defaultDocuments,
            ...(args?.documents ?? []),
        ])
        refetchDocuments()
    }

    const _onDownloadDynamicDocument = (document: Doc) => async () => {
        if (document.entityId && document.entityType === 'order') {
            if (
                typedArrayIncludes(
                    ['rateConfirmation', 'billOfLading'],
                    document.meta.dynamicDocType
                )
            ) {
                const blob = await getPdfBlob({
                    entityId: document.entityId,
                    entityType: document.entityType,
                    docDynamicType: document.meta.dynamicDocType,
                })

                downloadFile(
                    blob,
                    `${document.fileName} ${document.meta.orderFullId}.pdf`
                )
            }
        }
    }

    if (isLoadingDocuments) {
        return (
            <Column gap="p1">
                <Skeleton width={200} height={20} borderRadius={6} />
                <Skeleton width={200} height={20} borderRadius={6} />
            </Column>
        )
    }

    const documentIds = selectedThreadDocuments.map((document) => document.id)

    if (isReadOnly && defaultDocuments.length === 0) {
        return null
    }

    return (
        <Row gap="p1" justifyContent="space-between">
            <Column gap="p1" fullWidth>
                {defaultDocuments.length > 0 && (
                    <Typography color="primary" variant="body2">
                        Attachments:
                    </Typography>
                )}
                {defaultDocuments.length > 0 && (
                    <Table
                        isHeaderSticky={false}
                        isCompressed
                        withNoRowLines
                        rows={defaultDocuments}
                        columns={[
                            {
                                id: 'link',
                                label: 'Link',
                                width: 20,
                                renderCell: (document) => {
                                    if (
                                        document.entityType === 'order' &&
                                        typedArrayIncludes(
                                            [
                                                'rateConfirmation',
                                                'billOfLading',
                                            ],
                                            document.meta.dynamicDocType
                                        )
                                    ) {
                                        return (
                                            <IconButton
                                                onClick={_onDownloadDynamicDocument(
                                                    document
                                                )}
                                            >
                                                <FileIcon size={16} />
                                            </IconButton>
                                        )
                                    }

                                    return (
                                        <IconAnchorTag
                                            href={document.fileUrl}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                        >
                                            <FileIcon size={16} />
                                        </IconAnchorTag>
                                    )
                                },
                            },
                            {
                                id: 'connected',
                                label: 'From',
                                width: 20,
                                renderCell: (document) => {
                                    return (
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                        >
                                            {document.entityType === 'order'
                                                ? document.meta.orderFullId
                                                : 'Thread'}
                                        </Typography>
                                    )
                                },
                            },
                            {
                                id: 'name',
                                label: 'Name',
                                width: 80,
                                renderCell: (document) => (
                                    <Row gap="p1" alignItems="center">
                                        <TextCell>{document.fileName}</TextCell>
                                        {!isReadOnly && (
                                            <IconButton
                                                onClick={() =>
                                                    _onRemoveThreadDocument(
                                                        document.id
                                                    )
                                                }
                                            >
                                                <Trash2 size={16} />
                                            </IconButton>
                                        )}
                                    </Row>
                                ),
                            },
                        ]}
                    />
                )}
            </Column>
            {!isReadOnly && (
                <Popover
                    align="end"
                    trigger={
                        <Column alignItems="top">
                            <IconButton>
                                <Paperclip size={SPACING.p2} />
                            </IconButton>
                        </Column>
                    }
                    onClose={() => setPopupState('options')}
                >
                    {(onClose) => (
                        <Column fullWidth alignItems="center">
                            {popupState === 'pickDocument' && (
                                <StyledPopoverColumn gap="p2" p="p2">
                                    <Typography variant="body2" color="primary">
                                        Attach existing document
                                    </Typography>
                                    <Select
                                        isMulti
                                        placeholder="Attach documents"
                                        menuPlacement="top"
                                        value={documentIds}
                                        options={documentOptions}
                                        onSelectedOptionChanged={
                                            _onChangeSelectedThreadDocuments
                                        }
                                    />
                                    <Row
                                        fullWidth
                                        gap="p1"
                                        justifyContent="flex-end"
                                    >
                                        <Button
                                            variant="secondary"
                                            onClick={onClose}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={_onAttachSelectedDocuments(
                                                onClose
                                            )}
                                        >
                                            Attach
                                        </Button>
                                    </Row>
                                </StyledPopoverColumn>
                            )}
                            {popupState === 'options' && (
                                <>
                                    <AnchorTagStyled
                                        color="primary"
                                        target="_blank"
                                        onClick={_onShowPickDocument}
                                    >
                                        <RowStyled
                                            alignItems="center"
                                            justifyContent="flext-start"
                                        >
                                            <Typography
                                                variant="body2"
                                                color="primary"
                                            >
                                                Attach existing file
                                            </Typography>
                                        </RowStyled>
                                    </AnchorTagStyled>
                                    <Separator fullWidth />
                                    <AnchorTagStyled
                                        color="primary"
                                        target="_blank"
                                        onClick={() =>
                                            openDialog(
                                                'uploadDocumentsDialog',
                                                {
                                                    entityId: threadId,
                                                    entityType: 'thread',
                                                    customerId,
                                                    onSuccess: _onSuccessUpload,
                                                }
                                            )
                                        }
                                    >
                                        <RowStyled
                                            alignItems="center"
                                            justifyContent="flext-start"
                                        >
                                            <Typography
                                                variant="body2"
                                                color="primary"
                                            >
                                                Attach new file
                                            </Typography>
                                        </RowStyled>
                                    </AnchorTagStyled>
                                </>
                            )}
                        </Column>
                    )}
                </Popover>
            )}
        </Row>
    )
}
